import type { AnalyticsCardHeader } from '../../../analyticsModule/components/AnalyticsCard/AnalyticsCard.types'
import type { AnalyticsTableColumn } from '../../../analyticsModule/components/AnalyticsTable/AnalyticsTable.types'
import { useAnalyticsCardLegend } from '../../../hooks/useAnalyticsCardLegend'
import { useAnalyticsParams } from '../../../modules/AnalyticsParams/hooks/useAnalyticsParams'
import type { AnalyticsReportData, AnalyticsReportDefinition } from '../../../types'
import { createAnalyticsChartData } from '../../../utils/createAnalyticsChartData'
import { createChartXAxisFormatter } from '../../../utils/createChartXAxisFormatter'
import { getExternalSource } from '../../../utils/getExternalSource'
import { getSummaryValue } from '../../../utils/getSummaryValue'
import { formatChartValue } from './utils/formatChartValue'

type UseAnalyticsReportCardParams = {
  reportData: AnalyticsReportData | undefined
  columns: AnalyticsTableColumn[]
  reportDefinition: AnalyticsReportDefinition | undefined
  headerOptions: AnalyticsCardHeader['options']
  isReportDataLoading: boolean
  isReportDefinitionLoading: boolean
  activeSeries?: string
  setActiveSeries?: (series: string) => void
}

export const useAnalyticsReportCard = ({
  reportData,
  columns,
  reportDefinition,
  activeSeries,
  setActiveSeries,
  headerOptions,
  isReportDataLoading,
  isReportDefinitionLoading,
}: UseAnalyticsReportCardParams) => {
  const { precision, dateRange } = useAnalyticsParams()
  const activeColumn = columns.find((column) => column.id === activeSeries)
  const legend = useAnalyticsCardLegend()

  const getCardHeader = (): AnalyticsCardHeader => {
    if (!activeColumn) {
      return {
        label: 'Report',
      }
    }

    return {
      label: String(activeColumn.label) || '',
      tooltip: activeColumn.tooltip,
      options: headerOptions,
      onOptionSelect: setActiveSeries,
    }
  }

  if (reportDefinition?.graphType === 'none') {
    return undefined
  }

  return {
    isDataLoading: isReportDataLoading,
    isDefinitionLoading: isReportDefinitionLoading,
    type: (reportDefinition?.graphType === 'bar' ? 'barChart' : 'lineChart') as
      | 'barChart'
      | 'lineChart',
    data: createAnalyticsChartData({
      reportData,
      activeSeries: activeSeries || reportDefinition?.yAxisDefaultValue || '',
    }),
    emptyMessage: 'No data found in this date range',
    value: getSummaryValue({
      reportData,
      columnKey: activeSeries,
    }),
    header: getCardHeader(),
    legend,
    externalSource: getExternalSource(reportDefinition?.dataSource),
    formatValue: (value: number) => formatChartValue({ value, type: activeColumn?.type }),
    formatXAxis: createChartXAxisFormatter({
      columnType: columns[0]?.type,
      columnDecorator: columns[0]?.decorator,
      dateRange: dateRange.value.dates,
      precision: precision.value.value,
    }),
  }
}
